import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  HostedAgentLifecycleRequest,
  HostedAgentLifecycleRequestInput,
  HostedAgentLifecycleStatus,
} from "@/api/admin-api"

import {
  cancelHostedAgentLifecycleRequest,
  createHostedAgentLifecycleRequest,
  getHostedAgentLifecycleRequest,
  listHostedAgentLifecycleRequests,
  listTenantHostedAgentLifecycleRequests,
} from "@/api/admin-api"

import { GC_TIME } from "./_shared"

// Lifecycle requests (B10/B13). The portal is a pure observer: it queues a
// request and then polls while any request is non-terminal — the deploy
// worker on the customer VM does the actual work and reports back.

const HOSTED_AGENTS_KEY = ["admin", "hosted-agents"] as const
const LIFECYCLE_KEY = [...HOSTED_AGENTS_KEY, "lifecycle"] as const

/** Poll cadence while a request is pending/claimed/running (brief: ~3 s). */
export const HOSTED_AGENT_LIFECYCLE_POLL_MS = 3000

const NON_TERMINAL: ReadonlySet<HostedAgentLifecycleStatus> = new Set([
  "pending",
  "claimed",
  "running",
])

export function isHostedAgentLifecycleActive(status: HostedAgentLifecycleStatus): boolean {
  return NON_TERMINAL.has(status)
}

function pollWhileActive(requests: HostedAgentLifecycleRequest[] | undefined): number | false {
  return requests?.some((request) => isHostedAgentLifecycleActive(request.status))
    ? HOSTED_AGENT_LIFECYCLE_POLL_MS
    : false
}

/** Newest-first request history for one agent; polls while one is active. */
export function useAdminHostedAgentLifecycleRequestsQuery(
  agentId: string | undefined,
  enabled = true,
  limit = 20
) {
  return useQuery({
    queryKey: [...LIFECYCLE_KEY, "agent", agentId, limit],
    queryFn: () => listHostedAgentLifecycleRequests(agentId ?? "", limit),
    enabled: enabled && !!agentId,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchInterval: (query) => pollWhileActive(query.state.data),
  })
}

/**
 * Tenant-wide active requests (pending/claimed/running) — the table and the
 * map derive one status chip per agent from it. Polls while anything is
 * active so chips flip without a manual refresh.
 */
export function useAdminHostedAgentActiveLifecycleQuery(enabled = true) {
  return useQuery({
    queryKey: [...LIFECYCLE_KEY, "active"],
    queryFn: () => listTenantHostedAgentLifecycleRequests(["pending", "claimed", "running"]),
    enabled,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchInterval: (query) => pollWhileActive(query.state.data),
  })
}

export function useAdminHostedAgentLifecycleRequestQuery(
  requestId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [...LIFECYCLE_KEY, "request", requestId],
    queryFn: () => getHostedAgentLifecycleRequest(requestId ?? ""),
    enabled: enabled && !!requestId,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchInterval: (query) => {
      const request = query.state.data
      return request && isHostedAgentLifecycleActive(request.status)
        ? HOSTED_AGENT_LIFECYCLE_POLL_MS
        : false
    },
  })
}

function useInvalidateLifecycle() {
  const queryClient = useQueryClient()
  return async (agentId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: LIFECYCLE_KEY }),
      // The single-agent GET carries `lifecycle` and the pause state moves.
      queryClient.invalidateQueries({ queryKey: [...HOSTED_AGENTS_KEY, agentId] }),
      queryClient.invalidateQueries({ queryKey: HOSTED_AGENTS_KEY, exact: true }),
    ])
  }
}

export function useCreateHostedAgentLifecycleRequestMutation() {
  const invalidate = useInvalidateLifecycle()
  return useMutation({
    mutationFn: ({
      agentId,
      input,
    }: {
      agentId: string
      input: HostedAgentLifecycleRequestInput
    }) => createHostedAgentLifecycleRequest(agentId, input),
    onSuccess: (_request, { agentId }) => invalidate(agentId),
  })
}

export function useCancelHostedAgentLifecycleRequestMutation() {
  const invalidate = useInvalidateLifecycle()
  return useMutation({
    mutationFn: ({ requestId }: { agentId: string; requestId: string }) =>
      cancelHostedAgentLifecycleRequest(requestId),
    onSuccess: (_request, { agentId }) => invalidate(agentId),
  })
}
